// PFMEA Service - CRUD operations for PFMEA items
// Connects to Magnus-Core backend via authenticatedFetchJSON
// ============================================================

import { auth } from '../config/firebase';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

import { PfmeaItem } from '../types';

export interface ChangeRecord {
    timestamp: string;
    user: string;
    field: string;
    oldValue: string;
    newValue: string;
    comment?: string;
}

export interface PfmeaListResponse {
    items: PfmeaItem[];
    total: number;
    projectName: string;
}

// ============================================================
// CRUD Operations
// ============================================================

/**
 * Get all PFMEA items for a project
 */
export async function getPfmeaItems(projectName: string): Promise<PfmeaItem[]> {
    const data = await authenticatedFetchJSON<PfmeaListResponse>(
        `/api/pfmea/items?project=${encodeURIComponent(projectName)}`
    );
    return data.items || [];
}

/**
 * Get a single PFMEA item by ID
 */
export async function getPfmeaItem(id: string): Promise<PfmeaItem> {
    return authenticatedFetchJSON<PfmeaItem>(`/api/pfmea/items/${encodeURIComponent(id)}`);
}

/**
 * Create a new PFMEA item
 */
export async function createPfmeaItem(item: Omit<PfmeaItem, 'id'>): Promise<PfmeaItem> {
    const newItem = {
        ...item,
        id: `PFMEA-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.email || 'unknown',
        status: item.status || 'draft',
        changeHistory: [],
    };

    return authenticatedFetchJSON<PfmeaItem>('/api/pfmea/items', {
        method: 'POST',
        body: JSON.stringify(newItem),
    });
}

/**
 * Update an existing PFMEA item
 */
export async function updatePfmeaItem(
    id: string,
    updates: Partial<PfmeaItem>,
    changeRecord?: Omit<ChangeRecord, 'timestamp'>
): Promise<PfmeaItem> {
    const payload: any = {
        ...updates,
        updatedAt: new Date().toISOString(),
        modifiedBy: auth.currentUser?.email || 'unknown',
    };

    if (changeRecord) {
        const record: ChangeRecord = {
            ...changeRecord,
            timestamp: new Date().toISOString(),
        };
        // Use type assertion or default array to handle optional changeHistory
        payload.changeHistory = [...(updates.changeHistory || []), record];
    }

    return authenticatedFetchJSON<PfmeaItem>(`/api/pfmea/items/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

/**
 * Delete a PFMEA item
 */
export async function deletePfmeaItem(id: string): Promise<void> {
    await authenticatedFetchJSON<void>(`/api/pfmea/items/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
}

/**
 * Bulk save PFMEA items
 */
export async function bulkSavePfmeaItems(
    projectName: string,
    items: PfmeaItem[]
): Promise<PfmeaItem[]> {
    return authenticatedFetchJSON<PfmeaItem[]>('/api/pfmea/items/bulk', {
        method: 'POST',
        body: JSON.stringify({ projectName, items }),
    });
}

// ============================================================
// Workflow Operations
// ============================================================

export async function submitForReview(id: string, comment?: string): Promise<PfmeaItem> {
    return updatePfmeaItem(id, { status: 'review' }, {
        user: auth.currentUser?.email || 'unknown',
        field: 'status',
        oldValue: 'draft',
        newValue: 'review',
        comment: comment || 'Submitted for review',
    });
}

export async function approveItem(id: string, comment?: string): Promise<PfmeaItem> {
    return updatePfmeaItem(id, {
        status: 'approved',
        approvedBy: auth.currentUser?.email || 'unknown',
        approvedAt: new Date().toISOString(),
    }, {
        user: auth.currentUser?.email || 'unknown',
        field: 'status',
        oldValue: 'review',
        newValue: 'approved',
        comment: comment || 'Approved',
    });
}

export async function rejectItem(id: string, reason: string): Promise<PfmeaItem> {
    return updatePfmeaItem(id, { status: 'draft' }, {
        user: auth.currentUser?.email || 'unknown',
        field: 'status',
        oldValue: 'review',
        newValue: 'draft',
        comment: `Rejected: ${reason}`,
    });
}

// ============================================================
// Utility Functions
// ============================================================

export function calculateActionPriority(
    severity: number,
    occurrence: number,
    detection: number
): 'H' | 'M' | 'L' {
    if (severity >= 9) return 'H';
    if (severity >= 8 && occurrence >= 4) return 'H';
    if (severity >= 8 && detection >= 6) return 'H';
    if (severity >= 7 && occurrence >= 5 && detection >= 5) return 'H';

    if (severity >= 6 && occurrence >= 4) return 'M';
    if (severity >= 5 && occurrence >= 5) return 'M';
    if (severity >= 4 && occurrence >= 6) return 'M';

    return 'L';
}

export function calculateRPN(severity: number, occurrence: number, detection: number): number {
    return severity * occurrence * detection;
}

export function createEmptyPfmeaItem(projectName: string): Omit<PfmeaItem, 'id'> {
    return {
        projectName,
        standard: 'AIAG_VDA',
        processStepNumber: '',
        processStepName: '',
        processFunction: '',
        processElement: 'Method',
        failureMode: '',
        failureEffectEnd: '',
        failureCause: '',
        severity: 1,
        occurrence: 1,
        detection: 1,
        actionPriority: 'L',
        preventionControls: [],
        detectionControls: [],
        recommendedActions: [],
        status: 'draft',
        changeHistory: [],
        createdBy: auth.currentUser?.email || 'unknown',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };
}

export function duplicatePfmeaItem(item: PfmeaItem): Omit<PfmeaItem, 'id'> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, createdAt, createdBy, updatedAt, modifiedBy, approvedAt, approvedBy, changeHistory, ...rest } = item;
    return {
        ...rest,
        status: 'draft',
        changeHistory: [],
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.email || 'unknown',
        updatedAt: new Date().toISOString(),
    };
}

export default {
    getPfmeaItems,
    getPfmeaItem,
    createPfmeaItem,
    updatePfmeaItem,
    deletePfmeaItem,
    bulkSavePfmeaItems,
    submitForReview,
    approveItem,
    rejectItem,
    calculateActionPriority,
    calculateRPN,
    createEmptyPfmeaItem,
    duplicatePfmeaItem,
};
